import { memo, useLayoutEffect, useMemo, useRef } from 'react';
import { Text } from 'react-konva';
import Konva from 'konva';
import { useRefs } from 'contexts/RefContext';

import { KonvaGroupNames, getPolygonBoundingBox } from '../utils';

import { useKonvaImageScale } from 'contexts/ToolDimensionsAndScaleContext';
import polylabel from 'polylabel';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

type Props = {
  simplifiedHullCoords: number[][];
  label: string;
	visible: boolean;
	roofSegId: string;
};

const baseFontSize = 20;
// dont conditanley render it
// only control its visblity if u dont want to show it
const Label = memo(({ simplifiedHullCoords = [], label, visible, roofSegId }: Props) => {
	const scale = useKonvaImageScale();
	const isSaveInProgress = useSelector((state: RootState) => state.saveDesignState.isSaveInProgress);
	// font size follows the live stage zoom below, so re-render the label when zoomLevel changes
	useSelector((state: RootState) => state.toolNewPostions.data.zoomLevel);

	const { konvaRef } = useRefs();
	const absScale = konvaRef?.current?.getAbsoluteScale() || { x: 1, y: 1 };
	const zoom = absScale.x;

	const { x: cx, y: cy } = useMemo(
		() => {
			const { width, height } = getPolygonBoundingBox(simplifiedHullCoords);
			const precision = Math.max(Math.min(Math.min(width, height) / 10, 10), 1);
			const [px, py] = polylabel([simplifiedHullCoords], precision);
			return { x: px, y: py };
		},
		[simplifiedHullCoords]
	);
	
	const fontSize = useMemo(() => {
		// used to control fontsize that we send to parent as base64 via RoofImageBuilder
		if(isSaveInProgress) return 8;
		return Math.max(baseFontSize / zoom, 3);

	}, [isSaveInProgress, zoom]);

	// By default Konva draws text with its top-left at (x,y).
	//  Setting the offsets to half the measured width/height r
	// e-anchors the shape so that (x,y) sits at the geometric center of the text.
	const textRef = useRef<Konva.Text>(null);
	useLayoutEffect(() => {
		const node = textRef.current;
		if (node) {
			node.offsetX(node.width() / 2);
			node.offsetY(node.height() / 2);
		}
	}, [label, fontSize, zoom, simplifiedHullCoords]);

	return (
		<Text
			name={`${KonvaGroupNames.existingFacet}-label`}
			ref={textRef}
			text={label}
			x={cx * scale.x}
			y={cy * scale.y}
			fontSize={fontSize}
			fill="white"
			strokeScaleEnabled={false}
			listening={false}
			visible={visible}
			meta={{roofSegId: roofSegId }}
		/>
	);
});

Label.displayName = 'Label';
export default Label;